import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Select as SELECT } from "src/components/api/cart_details/cartDetails";

type CartItem = {
  CartMasterID: number;
  ProductMasterID: number;
  ProductName: string;
  BrandName: string;
  Quantity: number;
  Price: string | number;
  ImagePaths: string[];
};

type CartState = {
  /** Total quantity across all cart lines (sum of Quantity). */
  count: number;
  /** Number of distinct product lines in the cart. */
  lineCount: number;
  items: CartItem[];
};

const initialState: CartState = {
  count: 0,
  lineCount: 0,
  items: [],
};

function syncCountsFromItems(state: CartState) {
  state.count = state.items.reduce((sum, item) => sum + item.Quantity, 0);
  state.lineCount = state.items.length;
}

export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async () => {
    const response = await SELECT();
    if (!response?.status) {
      throw new Error('Failed to fetch cart');
    }
    const parsed = JSON.parse(response?.data!);
    return { items: parsed.list || [] };
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCartCount: (state, action: PayloadAction<number>) => {
      state.count = action.payload;
    },
    addCartItem: (state, action: PayloadAction<CartItem>) => {
      const incoming = action.payload;
      const existing = state.items.find(
        (item) => item.ProductMasterID === incoming.ProductMasterID,
      );
      if (existing) {
        existing.Quantity += incoming.Quantity;
      } else {
        state.items.push(incoming);
      }
      syncCountsFromItems(state);
    },
    setCartItems: (state, action: PayloadAction<CartItem[]>) => {
      state.items = action.payload;
      syncCountsFromItems(state);
    },
    clearCartCount: (state) => {
      state.count = 0;
      state.lineCount = 0;
    },
    clearCartItems: (state) => {
      state.items = [];
      state.count = 0;
      state.lineCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.items = action.payload.items;
        syncCountsFromItems(state);
      })
      .addCase(fetchCart.rejected, (state) => {
        state.count = 0;
        state.lineCount = 0;
        state.items = [];
      });
  },
});

export const {
  setCartCount,
  addCartItem,
  setCartItems,
  clearCartCount,
  clearCartItems,
} = cartSlice.actions;
export const selectCartCount = (state: { cart: CartState }) => state.cart.count;
export const selectCartLineCount = (state: { cart: CartState }) =>
  state.cart.lineCount ?? state.cart.items.length;
export const selectCartItems = (state: { cart: CartState }) => state.cart.items;
export default cartSlice.reducer;
